import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import { useBusinessTheme, BusinessTheme, themeToCSSVariables, generateGoogleFontsUrl } from '@/hooks/business/useBusinessTheme';

interface BusinessThemeContextValue {
  theme: BusinessTheme | null;
  loading: boolean;
  error: string | null;
  cssVariables: Record<string, string>;
  hasTheme: boolean;
}

const BusinessThemeContext = createContext<BusinessThemeContextValue | undefined>(undefined);

interface BusinessThemeProviderProps {
  children: ReactNode;
  businessId?: string;
  /** Apply theme to document root (for full-page themes) */
  applyToRoot?: boolean;
  /** Fallback theme when no business theme exists */
  fallbackTheme?: Partial<BusinessTheme>;
}

const DEFAULT_FALLBACK_THEME: Partial<BusinessTheme> = {
  primary_color: '#000000',
  secondary_color: '#666666',
  accent_color: '#0066FF',
  background_color: '#FFFFFF',
  text_color: '#000000',
  heading_font: 'Inter',
  body_font: 'Inter',
};

export function BusinessThemeProvider({
  children,
  businessId,
  applyToRoot = false,
  fallbackTheme = DEFAULT_FALLBACK_THEME,
}: BusinessThemeProviderProps) {
  const { theme, loading, error } = useBusinessTheme({ businessId, autoFetch: !!businessId });
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // Merge theme with fallback
  const effectiveTheme = useMemo(() => {
    if (theme) return theme;
    return { ...DEFAULT_FALLBACK_THEME, ...fallbackTheme } as BusinessTheme;
  }, [theme, fallbackTheme]);

  // Generate CSS variables
  const cssVariables = useMemo(() => {
    return themeToCSSVariables(effectiveTheme);
  }, [effectiveTheme]);

  // Load Google Fonts if custom fonts are used
  useEffect(() => {
    if (!effectiveTheme) return;

    const fontsUrl = generateGoogleFontsUrl(effectiveTheme);
    if (!fontsUrl) {
      setFontsLoaded(true);
      return;
    }

    // Check if fonts link already exists
    const existingLink = document.querySelector(`link[href*="fonts.googleapis.com"]`);
    if (existingLink) {
      setFontsLoaded(true);
      return;
    }

    // Create and append fonts link
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = fontsUrl;
    link.onload = () => setFontsLoaded(true);
    link.onerror = () => setFontsLoaded(true); // Continue even if fonts fail
    document.head.appendChild(link);

    return () => {
      // Don't remove fonts as they might be used elsewhere
    };
  }, [effectiveTheme]);

  // Apply to document root if requested
  useEffect(() => {
    if (!applyToRoot || !cssVariables) return;

    const root = document.documentElement;
    Object.entries(cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    return () => {
      // Clean up on unmount
      Object.keys(cssVariables).forEach(key => {
        root.style.removeProperty(key);
      });
    };
  }, [applyToRoot, cssVariables]);

  const contextValue: BusinessThemeContextValue = {
    theme,
    loading: loading || !fontsLoaded,
    error,
    cssVariables,
    hasTheme: !!theme,
  };

  // Inline style for wrapping div (when not applying to root)
  const wrapperStyle = !applyToRoot ? cssVariables : undefined;

  return (
    <BusinessThemeContext.Provider value={contextValue}>
      {applyToRoot ? (
        children
      ) : (
        <div style={wrapperStyle as React.CSSProperties} className="business-theme-wrapper">
          {children}
        </div>
      )}
    </BusinessThemeContext.Provider>
  );
}

export function useBusinessThemeContext(): BusinessThemeContextValue {
  const context = useContext(BusinessThemeContext);
  if (context === undefined) {
    // Return default values if used outside provider
    return {
      theme: null,
      loading: false,
      error: null,
      cssVariables: themeToCSSVariables(DEFAULT_FALLBACK_THEME as BusinessTheme),
      hasTheme: false,
    };
  }
  return context;
}

/**
 * Component for displaying themed content with business branding
 * Use this to wrap forms, cards, etc. that should use business colors
 */
interface ThemedContainerProps {
  children: ReactNode;
  className?: string;
  useAccentBackground?: boolean;
  usePrimaryBackground?: boolean;
}

export function ThemedContainer({
  children,
  className = '',
  useAccentBackground = false,
  usePrimaryBackground = false,
}: ThemedContainerProps) {
  const { cssVariables } = useBusinessThemeContext();

  const style: React.CSSProperties = {
    ...cssVariables as React.CSSProperties,
    fontFamily: `var(--business-body-font, Inter), system-ui, sans-serif`,
    color: `var(--business-text, #000000)`,
    ...(useAccentBackground && { backgroundColor: `var(--business-accent, #0066FF)` }),
    ...(usePrimaryBackground && { backgroundColor: `var(--business-primary, #000000)` }),
  };

  return (
    <div style={style} className={className}>
      {children}
    </div>
  );
}

/**
 * Themed button component
 */
interface ThemedButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent';
}

export function ThemedButton({
  children,
  variant = 'accent',
  className = '',
  style: customStyle,
  ...props
}: ThemedButtonProps) {
  const baseStyle: React.CSSProperties = {
    fontFamily: `var(--business-body-font, Inter), system-ui, sans-serif`,
    cursor: 'pointer',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 500,
    transition: 'opacity 0.2s ease',
    ...customStyle,
  };

  const variantStyles: Record<string, React.CSSProperties> = {
    primary: {
      backgroundColor: `var(--business-primary, #000000)`,
      color: `var(--business-background, #FFFFFF)`,
    },
    secondary: {
      backgroundColor: `var(--business-secondary, #666666)`,
      color: `var(--business-background, #FFFFFF)`,
    },
    accent: {
      backgroundColor: `var(--business-accent, #0066FF)`,
      color: `var(--business-background, #FFFFFF)`,
    },
  };

  return (
    <button
      style={{ ...baseStyle, ...variantStyles[variant] }}
      className={className}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * Themed heading component
 */
interface ThemedHeadingProps {
  children: ReactNode;
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
  style?: React.CSSProperties;
}

export function ThemedHeading({
  children,
  level = 1,
  className = '',
  style: customStyle,
}: ThemedHeadingProps) {
  const Tag = `h${level}` as keyof JSX.IntrinsicElements;

  const style: React.CSSProperties = {
    fontFamily: `var(--business-heading-font, Inter), system-ui, sans-serif`,
    color: `var(--business-text, #000000)`,
    margin: 0,
    ...customStyle,
  };

  return <Tag style={style} className={className}>{children}</Tag>;
}

/**
 * Themed text component
 */
interface ThemedTextProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  muted?: boolean;
}

export function ThemedText({
  children,
  className = '',
  style: customStyle,
  muted = false,
}: ThemedTextProps) {
  const style: React.CSSProperties = {
    fontFamily: `var(--business-body-font, Inter), system-ui, sans-serif`,
    color: muted
      ? `var(--business-secondary, #666666)`
      : `var(--business-text, #000000)`,
    margin: 0,
    ...customStyle,
  };

  return <p style={style} className={className}>{children}</p>;
}

export { BusinessThemeContext };
